import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { useUCMenuItems } from '@atlassian/navigation-bff-data-provider/universal-create';

import { useMenuStateContext } from '../../../../context';
import { sectionsToAppMenuConfig } from '../../MenuContentV2/utils/configConverters';

import { useFetchUser } from './useFetchUser';
import { usePrefetchLoom } from './usePrefetchLoom';

interface usePrefetchV2Return {
	triggerPrefetch: () => void;
}

/**
 * V2 of prefetch that will try to call the backend endpoint to get list of menu items
 * Loom is NOT included. TODO: add loom preload separately https://hello.jira.atlassian.cloud/browse/NAVX-2470
 * User is conditionally fetched based on return items from the backend
 *
 * @returns Object with triggerPrefetch function and prefetch requirement flags
 */
export function usePrefetchV2(): usePrefetchV2Return {
	const { sections, product, subProduct, crossAppCreationConfig } = useMenuStateContext();
	const crossAppCreationEnabled = fg('navx_uc_cross_app_creation_v2_enabled');
	const { locale } = useIntl();

	const shouldSkip = !crossAppCreationEnabled || !crossAppCreationConfig.enabled;
	const shouldFetchData =
		!shouldSkip && crossAppCreationConfig.enabled && crossAppCreationConfig.prefetch === true;

	const { triggerPrefetch: triggerPrefetchLoom } = usePrefetchLoom();

	const appMenuConfig = useMemo(() => sectionsToAppMenuConfig(sections), [sections]);
	const { fetchUserIfNeeded } = useFetchUser();

	/**
	 * Fetching V2 version of menu items
	 */
	const { data, fetchUCMenuItems } = useUCMenuItems({
		cloudId: crossAppCreationConfig.enabled ? crossAppCreationConfig.cloudId : '',
		orgId: crossAppCreationConfig.enabled ? crossAppCreationConfig.orgId : '',
		userId: crossAppCreationConfig.enabled ? crossAppCreationConfig.accountId : '',
		product,
		locale,
		subProduct,
		appMenuConfig,
		skipInitialization: !shouldFetchData,
	});

	const prefetchTriggered = useRef(false);
	const userFetchTriggered = useRef(false);

	const triggerPrefetch = useCallback(() => {
		if (prefetchTriggered.current || !shouldFetchData) {
			return;
		}
		prefetchTriggered.current = true;
		triggerPrefetchLoom();
		fetchUCMenuItems();

		// Fallback: fetch user immediately if reactive fetching is not enabled
		if (!fg('navx_uc_improve_user_fetching')) {
			fetchUserIfNeeded({ sections: data?.sections });
		}
	}, [shouldFetchData, fetchUCMenuItems, triggerPrefetchLoom, fetchUserIfNeeded, data?.sections]);

	// Reactively fetch user data when sections become available
	useEffect(
		function fetchUserWhenDataReady() {
			if (!fg('navx_uc_improve_user_fetching') || !shouldFetchData || !data?.sections || userFetchTriggered.current) {
				return;
			}

			userFetchTriggered.current = true;
			fetchUserIfNeeded({ sections: data.sections });
		},
		[shouldFetchData, data?.sections, fetchUserIfNeeded],
	);

	return {
		triggerPrefetch,
	};
}
